// store/slices/collectionsSlice.ts
// Manages collection state for organizing media items
import { StateCreator } from "zustand";
import * as collectionsService from "@/services/collectionsService";
import { Collection } from "@/services/collectionsService";

export interface CollectionsState {
  collections: Collection[];
  activeCollectionId: string | null;
  collectionItemIds: Set<number>; // Cached item IDs for active collection
  isLoading: boolean;
  isLoadingItems: boolean;
  error: string | null;
  lastFetchedClientId: string | null;
  isCollectionMode: boolean; // Track if collection mode is active
}

export interface CollectionsSlice {
  collections: CollectionsState;

  // Collection list management
  fetchCollections: (clientId: string) => Promise<void>;
  clearCollections: () => void;

  // Active collection management
  setActiveCollection: (collectionId: string | null) => void;
  fetchActiveCollectionItems: (clientId: string) => Promise<void>;

  // CRUD operations
  createCollection: (
    clientId: string,
    data: { name?: string; briefId?: string; ideaId?: string }
  ) => Promise<Collection>;
  updateCollection: (
    clientId: string,
    collectionId: string,
    data: { name?: string | null; briefId?: string | null; ideaId?: string | null }
  ) => Promise<Collection>;
  deleteCollection: (clientId: string, collectionId: string) => Promise<void>;

  // Item operations on active collection
  addItemsToActiveCollection: (
    clientId: string,
    mediaItemIds: number[]
  ) => Promise<void>;
  removeItemsFromActiveCollection: (
    clientId: string,
    mediaItemIds: number[]
  ) => Promise<void>;
  toggleItemInActiveCollection: (
    clientId: string,
    mediaItemId: number
  ) => Promise<void>;

  // Query helpers
  isInActiveCollection: (mediaItemId: number) => boolean;
  getActiveCollection: () => Collection | null;

  // Collection mode
  enterCollectionMode: () => void;
  exitCollectionMode: () => void;
}

const initialState: CollectionsState = {
  collections: [],
  activeCollectionId: null,
  collectionItemIds: new Set(),
  isLoading: false,
  isLoadingItems: false,
  error: null,
  lastFetchedClientId: null,
  isCollectionMode: false,
};

export const createCollectionsSlice: StateCreator<CollectionsSlice> = (set, get) => ({
  collections: initialState,

  fetchCollections: async (clientId: string) => {
    set((state) => ({
      collections: {
        ...state.collections,
        isLoading: true,
        error: null,
      },
    }));

    try {
      const collections = await collectionsService.getCollections(clientId);
      set((state) => ({
        collections: {
          ...state.collections,
          collections,
          isLoading: false,
          lastFetchedClientId: clientId,
        },
      }));
    } catch (err) {
      set((state) => ({
        collections: {
          ...state.collections,
          isLoading: false,
          error: err instanceof Error ? err.message : "Failed to fetch collections",
        },
      }));
    }
  },

  clearCollections: () => {
    set({
      collections: initialState,
    });
  },

  setActiveCollection: (collectionId: string | null) => {
    set((state) => ({
      collections: {
        ...state.collections,
        activeCollectionId: collectionId,
        collectionItemIds: new Set(), // Clear cached items
      },
    }));
  },

  fetchActiveCollectionItems: async (clientId: string) => {
    const state = get();
    const activeId = state.collections.activeCollectionId;
    if (!activeId) return;

    set((state) => ({
      collections: {
        ...state.collections,
        isLoadingItems: true,
      },
    }));

    try {
      const itemIds = await collectionsService.getCollectionItemIds(clientId, activeId);
      set((state) => ({
        collections: {
          ...state.collections,
          collectionItemIds: new Set(itemIds),
          isLoadingItems: false,
        },
      }));
    } catch (err) {
      console.error("Failed to fetch collection items:", err);
      set((state) => ({
        collections: {
          ...state.collections,
          isLoadingItems: false,
        },
      }));
    }
  },

  createCollection: async (clientId, data) => {
    const collection = await collectionsService.createCollection(clientId, data);
    set((state) => ({
      collections: {
        ...state.collections,
        collections: [collection, ...state.collections.collections],
      },
    }));
    return collection;
  },

  updateCollection: async (clientId, collectionId, data) => {
    const updated = await collectionsService.updateCollection(clientId, collectionId, data);
    set((state) => ({
      collections: {
        ...state.collections,
        collections: state.collections.collections.map((c) =>
          c.id === collectionId ? updated : c
        ),
      },
    }));
    return updated;
  },

  deleteCollection: async (clientId, collectionId) => {
    await collectionsService.deleteCollection(clientId, collectionId);
    set((state) => {
      const newState: CollectionsState = {
        ...state.collections,
        collections: state.collections.collections.filter((c) => c.id !== collectionId),
      };
      // Clear active collection if it was deleted
      if (state.collections.activeCollectionId === collectionId) {
        newState.activeCollectionId = null;
        newState.collectionItemIds = new Set();
      }
      return { collections: newState };
    });
  },

  addItemsToActiveCollection: async (clientId, mediaItemIds) => {
    const state = get();
    const activeId = state.collections.activeCollectionId;
    if (!activeId) return;

    // Optimistic update
    const newItemIds = new Set(state.collections.collectionItemIds);
    mediaItemIds.forEach((id) => newItemIds.add(id));
    set((state) => ({
      collections: {
        ...state.collections,
        collectionItemIds: newItemIds,
        // Update item count optimistically
        collections: state.collections.collections.map((c) =>
          c.id === activeId
            ? { ...c, itemCount: c.itemCount + mediaItemIds.length }
            : c
        ),
      },
    }));

    try {
      const result = await collectionsService.addItemsToCollection(
        clientId,
        activeId,
        mediaItemIds
      );
      // Update with actual count
      set((state) => ({
        collections: {
          ...state.collections,
          collections: state.collections.collections.map((c) =>
            c.id === activeId
              ? { ...c, itemCount: c.itemCount - mediaItemIds.length + result.addedCount }
              : c
          ),
        },
      }));
    } catch (err) {
      // Revert on error
      set((state) => ({
        collections: {
          ...state.collections,
          collectionItemIds: new Set(
            Array.from(state.collections.collectionItemIds).filter((id) => !mediaItemIds.includes(id))
          ),
          collections: state.collections.collections.map((c) =>
            c.id === activeId
              ? { ...c, itemCount: c.itemCount - mediaItemIds.length }
              : c
          ),
        },
      }));
      throw err;
    }
  },

  removeItemsFromActiveCollection: async (clientId, mediaItemIds) => {
    const state = get();
    const activeId = state.collections.activeCollectionId;
    if (!activeId) return;

    // Optimistic update
    const newItemIds = new Set(
      Array.from(state.collections.collectionItemIds).filter((id) => !mediaItemIds.includes(id))
    );
    set((state) => ({
      collections: {
        ...state.collections,
        collectionItemIds: newItemIds,
        // Update item count optimistically
        collections: state.collections.collections.map((c) =>
          c.id === activeId
            ? { ...c, itemCount: Math.max(0, c.itemCount - mediaItemIds.length) }
            : c
        ),
      },
    }));

    try {
      await collectionsService.removeItemsFromCollection(
        clientId,
        activeId,
        mediaItemIds
      );
    } catch (err) {
      // Revert on error
      const revertedIds = new Set(state.collections.collectionItemIds);
      mediaItemIds.forEach((id) => revertedIds.add(id));
      set((state) => ({
        collections: {
          ...state.collections,
          collectionItemIds: revertedIds,
          collections: state.collections.collections.map((c) =>
            c.id === activeId
              ? { ...c, itemCount: c.itemCount + mediaItemIds.length }
              : c
          ),
        },
      }));
      throw err;
    }
  },

  toggleItemInActiveCollection: async (clientId, mediaItemId) => {
    const state = get();
    const isInCollection = state.collections.collectionItemIds.has(mediaItemId);

    if (isInCollection) {
      await get().removeItemsFromActiveCollection(clientId, [mediaItemId]);
    } else {
      await get().addItemsToActiveCollection(clientId, [mediaItemId]);
    }
  },

  isInActiveCollection: (mediaItemId: number) => {
    return get().collections.collectionItemIds.has(mediaItemId);
  },

  getActiveCollection: () => {
    const state = get();
    const activeId = state.collections.activeCollectionId;
    if (!activeId) return null;
    return state.collections.collections.find((c) => c.id === activeId) || null;
  },

  enterCollectionMode: () => {
    set((state) => ({
      collections: {
        ...state.collections,
        isCollectionMode: true,
      },
    }));
  },

  exitCollectionMode: () => {
    set((state) => ({
      collections: {
        ...state.collections,
        isCollectionMode: false,
      },
    }));
  },
});
